import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import EmployeeShell from "../../components/layout/EmployeeShell";
import RefundRuleCard from "../../components/RefundRuleCard";
import StatusBadge from "../../components/StatusBadge";
import UploadPanel from "../../components/UploadPanel";
import { useI18n } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { DashboardApi, ReceiptsApi } from "../../lib/api";
import { formatMoney, relativeTime } from "../../lib/format";

const EmployeeScan = () => {
  const { t } = useI18n();
  const { currency, user, isAccountingFirm } = useAuth();
  const [recent, setRecent] = useState([]);
  const [summary, setSummary] = useState(null);
  const [quota, setQuota] = useState(null);

  const load = useCallback(() => {
    ReceiptsApi.quota()
      .then(setQuota)
      .catch(() => setQuota(null));
    ReceiptsApi.list({ limit: 5 })
      .then((d) => setRecent(d.receipts || []))
      .catch(() => setRecent([]));
    DashboardApi.mySummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <EmployeeShell
      title={t("employee.greeting", { name: (user?.name || "").split(" ")[0] || "" })}
      subtitle={t("employee.sub")}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="ll-card p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("employee.myExpenses")}</div>
          <div className="ll-num mt-1 font-display text-xl font-semibold" data-testid="employee-total-expenses">
            {formatMoney(summary?.totals?.gross, currency)}
          </div>
          <p className="text-xs text-muted-foreground">
            {summary?.totals?.count || 0} {t("common.receipts").toLowerCase()}
          </p>
        </div>
        {!isAccountingFirm ? (
        <div className="ll-card p-3">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">{t("employee.myRefund")}</div>
          <div
            className="ll-num mt-1 font-display text-xl font-semibold text-primary"
            data-testid="employee-total-refund"
          >
            {formatMoney(summary?.totals?.refund, currency)}
          </div>
          <p className="text-xs text-muted-foreground">
            {formatMoney(summary?.totals?.refund_approved, currency)} {t("employee.approvedWord")}
          </p>
        </div>
        ) : null}
      </div>

      <div className="mt-4">
        <UploadPanel
          currency={currency}
          onParsed={load}
          compact
          showSamples={!isAccountingFirm}
          quota={quota}
        />
      </div>

      <div className="ll-card mt-4 overflow-hidden">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="font-display text-sm font-semibold">{t("employee.lastReceipts")}</h2>
          <Link
            to="/me/receipts"
            className="flex items-center gap-1 text-xs font-medium text-primary"
            data-testid="employee-see-all-link"
          >
            {t("employee.seeAll")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        {recent.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">
            {t("employee.nothingYet")}
          </p>
        ) : (
          <ul className="divide-y" data-testid="employee-recent-receipts-list">
            {recent.map((r) => (
              <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{r.vendor_name || "Receipt"}</p>
                  <p className="text-xs text-muted-foreground">{relativeTime(r.created_at)}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="ll-num text-sm font-semibold">
                    {r.gross_total != null ? formatMoney(r.gross_total, r.currency || currency) : "—"}
                  </p>
                  <StatusBadge status={r.status} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {summary?.my_rule && !isAccountingFirm ? (
        <div className="mt-4">
          <RefundRuleCard rule={summary.my_rule} currency={currency} compact />
        </div>
      ) : null}
    </EmployeeShell>
  );
};

export default EmployeeScan;
